import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { Container, ErrorMessage, Loading, SimpleButton, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ExamsTable from './components/ExamsTable';
import ViewExamsFilters from './components/ViewExamsFilters';
import PublishModal from './components/PublishModal';
import DeleteExamModal from './components/DeleteExamModal';
import { type FilterState } from './models/filterDTOs';
import { type ExamDTO } from './models/examDTOs';
import { useAuth } from '@/src/app/context/useAuth';

const ViewExamsPage: React.FC = () => {
    document.title = 'Exams';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [searchString, setSearchString] = useState('');
    const [data, setData] = useState<ExamDTO[]>([]);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const FILTER_KEY = `exams-filters-${userId}`;

    const [filterState, setFilterState] = useState(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) return JSON.parse(saved);
        } catch {
            // intentionally empty
        }
        return {
            examTypeMasterId: 0,
            gradeLevelMasterId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            localStorage.setItem(FILTER_KEY, JSON.stringify(filterState));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, FILTER_KEY]);

    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedExam, setSelectedExam] = useState<ExamDTO | null>(null);

    const navigate = useNavigate();

    const refreshTableData = useCallback(
        async ({ gradeLevelMasterId }: FilterState = filterState) => {
            setError('');
            setLoaded(false);

            setSearchString('');

            let requestUrl = `assessments/exams`;
            requestUrl += `?gradeLevelMasterIds=${gradeLevelMasterId}`;

            try {
                const res = await apiRequest<ExamDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch exam data');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    const handleModalClose = useCallback(
        (shouldRefresh: boolean, isInTimeout: boolean) => {
            setShowDeleteModal(false);
            setShowPublishModal(false);
            // Don't allow the modal to be reopened while it is in its post-submit timeout
            if (!isInTimeout) {
                setSelectedExam(null);
            }

            if (shouldRefresh) {
                refreshTableData();
            }
        },
        [refreshTableData]
    );

    useEffect(() => {
        (async function init() {
            await refreshTableData();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Exams</h1>
            <br />

            <ViewExamsFilters
                refreshTableData={refreshTableData}
                setSearchString={setSearchString}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchExams"
                    label="Search by title..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />

                <SimpleButton
                    onClick={() => navigate(`/examsAdmin/createEditExam?viewMode=create`)}
                    variant="contained"
                    color="primary"
                    className="whitespace-nowrap"
                >
                    Add Exam
                </SimpleButton>
            </Box>

            <Loading visible={!loaded} message="Loading assessments..." />
            <ErrorMessage error={error} style={{ textAlign: 'center' }} />

            {loaded && (
                <ExamsTable
                    data={data}
                    showPublishModal={showPublishModal}
                    setShowPublishModal={setShowPublishModal}
                    showDeleteModal={showDeleteModal}
                    setShowDeleteModal={setShowDeleteModal}
                    setSelectedExam={setSelectedExam}
                    searchString={searchString}
                />
            )}

            {showPublishModal && selectedExam?.id && (
                <PublishModal resourceId={selectedExam?.id} handleClose={handleModalClose} />
            )}
            {showDeleteModal && selectedExam?.id && (
                <DeleteExamModal resourceId={selectedExam?.id} handleClose={handleModalClose} />
            )}
        </Container>
    );
};

export default ViewExamsPage;
